import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextInput from 'components/form/TextInput';
import Switch from 'components/form/Switch';
import Select from 'components/form/Select';
import Typography from '@mui/material/Typography';
import { getConnectCards } from 'services/connect-card.service';
import { getVideoPages } from 'services/videoPages.service';
import { useSelector } from 'react-redux';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { getConnectCardUrl } from 'modules/connect-card/utils';
import RichTextEditor from 'components/form/RichTextEditor';
import CreateVideoStep from './CreateVideoStep';

const LINK_OPTIONS = [
  { label: 'URL', value: 'url' },
  { label: 'Connect Card', value: 'connect_card' },
  { label: 'Page', value: 'video_page' },
];

const CONNECT_CARD_DISPLAY_OPTION = [
  { label: 'Open in pop up', value: '1' },
  { label: 'Display on page', value: '0' },
];

const ConnectCardOptions = ({
  value,
  onChange,
  onDisplayTypeChange,
  displayType,
}) => {
  const [cards, setCards] = useState([]);
  const account = useSelector((s) => s.settings.account);

  useEffect(() => {
    getConnectCards({ account_id: account.account_id }).then((resp) => {
      setCards(
        resp.map((i) => ({
          label: i.card_name || 'Unnamed card',
          value: getConnectCardUrl(i),
        })),
      );
    });
  }, []);

  return (
    <Box>
      <RadioGroup
        row
        sx={{ mt: 1 }}
        value={displayType}
        onChange={(e) => onDisplayTypeChange(e.target.value)}
      >
        {CONNECT_CARD_DISPLAY_OPTION.map((option, index) => {
          return (
            <FormControlLabel
              key={index}
              value={option.value}
              control={<Radio size="small" />}
              label={option.label}
            />
          );
        })}
      </RadioGroup>
      <Select
        fullWidth
        transformValue
        isMulti={false}
        options={cards}
        value={value}
        onChange={onChange}
        placeholder="Select Connect Card"
        margin="none"
      />
    </Box>
  );
};

const VideoPageOptions = ({ value, onChange }) => {
  const params = useParams();
  const [cards, setCards] = useState([]);
  useEffect(() => {
    getVideoPages().then((resp) => {
      setCards(
        resp
          .filter((i) => i.hash !== params.id)
          .map((i) => ({
            label: i.headline,
            value: i.redirect.qr_code_text,
          })),
      );
    });
  }, []);

  return (
    <Box>
      <Select
        fullWidth
        transformValue
        isMulti={false}
        options={cards}
        value={value}
        onChange={onChange}
        margin="none"
      />
    </Box>
  );
};

const CustomizePage = ({
  values,
  onChange,
  onRemove,
  uploadProgress,
  hideVideoOptions,
}) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange(name, value);
  };

  const onSwitchChange = (e) => {
    const { name, checked } = e.target;
    onChange(name, checked ? '1' : '0');
  };

  const handleLinkTypeChange = (e) => {
    const { value } = e.target;
    onChange('link_to', value);
    onChange('call_to_action', '');
  };

  return (
    <Box>
      <TextInput
        fullWidth
        name="headline"
        label="Headline"
        onChange={handleInputChange}
        value={values.headline}
        placeholder="Headline"
      />
      {!hideVideoOptions && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight={500}>Include Video</Typography>
          <Switch
            labelPlacement="start"
            size="small"
            name="enableVideo"
            margin="none"
            checked={values.enableVideo === '1'}
            onChange={onSwitchChange}
          />
        </Stack>
      )}
      {values.enableVideo === '1' && (
        <CreateVideoStep
          values={values}
          onChange={onChange}
          onRestart={onRemove}
          uploadProgress={uploadProgress}
        />
      )}
      <RichTextEditor
        name="message"
        label="Note"
        placeholder="Add your notes here..."
        onChange={(v) => {
          handleInputChange({
            target: {
              name: 'message',
              value: v,
            },
          });
        }}
        value={values.message}
        sx={{ borderRadius: 1 }}
        height={300}
      />

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography fontWeight={500}>Button design</Typography>
        <Switch
          size="small"
          margin="none"
          name="call_to_action_enabled"
          checked={values.call_to_action_enabled === '1'}
          onChange={onSwitchChange}
          labelPlacement="start"
        />
      </Box>
      {values.call_to_action_enabled === '1' && (
        <>
          <TextInput
            fullWidth
            margin="dense"
            name="call_to_action_text"
            placeholder="Button text"
            onChange={handleInputChange}
            value={values.call_to_action_text}
            sx={{ mb: 2 }}
          />
          <Box display="flex" alignItems="center" mb={2} mt={2}>
            <Typography
              sx={{
                mr: 1,
              }}
            >
              Link to:
            </Typography>
            <RadioGroup
              row
              sx={{ marginLeft: 1 }}
              value={values?.link_to}
              onChange={handleLinkTypeChange}
            >
              {LINK_OPTIONS.map((option, index) => {
                return (
                  <FormControlLabel
                    key={index}
                    value={option.value}
                    control={
                      <Radio
                        sx={{ padding: 0, paddingInline: '5px' }}
                        checked={values?.link_to === option.value}
                      />
                    }
                    label={
                      <Typography variant="body2">{option.label}</Typography>
                    }
                  />
                );
              })}
            </RadioGroup>
          </Box>
          {values.link_to === 'connect_card' && (
            <ConnectCardOptions
              value={values.call_to_action}
              displayType={values.is_popup_view}
              onDisplayTypeChange={(val) => onChange('is_popup_view', val)}
              onChange={(val) => onChange('call_to_action', val)}
            />
          )}
          {values.link_to === 'video_page' && (
            <VideoPageOptions
              value={values.call_to_action}
              onChange={(val) => onChange('call_to_action', val)}
            />
          )}
          {values.link_to === 'url' && (
            <TextInput
              fullWidth
              margin="none"
              name="call_to_action"
              value={values.call_to_action}
              onChange={handleInputChange}
              placeholder="Button link"
            />
          )}
        </>
      )}
    </Box>
  );
};

export default CustomizePage;
